import { __awaiter, __generator, __read, __values } from "tslib";
import { TransactionMode, QueryAggregateType, isKey, WhereOperator, } from '@tencent/memoire-core';
import { ClientQueryConditionType, Command, } from './client-msg.interface';
import { NativeEngineCursor } from './cursor';
import { NativeEngineIndex } from './database-index';
import { IndexNotExist, WhereOrUnsupportedError } from './error';
import { ClientProxy } from './client-proxy';
import { QueryConditionBuilder } from './utils/query-condition-builder';
import { KeyUtils } from './utils/key-utils';
import { assertResp } from './utils/common';
var NativeEngineTable = /** @class */ (function () {
    function NativeEngineTable(dbName, collectionSchema, mode, engineAspects, transaction) {
        var _this = this;
        var _a, _b, _c;
        this.dbName = dbName;
        this.collectionSchema = collectionSchema;
        this.mode = mode;
        this.engineAspects = engineAspects;
        this.transaction = transaction;
        this.createIndex = undefined;
        this.delete = undefined;
        this.bulkDelete = undefined;
        this.clear = undefined;
        this.insert = undefined;
        this.upsert = undefined;
        this.deleteIndex = undefined;
        this.bulkUpsert = undefined;
        this.keyPath = (_b = (_a = collectionSchema.key) === null || _a === void 0 ? void 0 : _a.path) !== null && _b !== void 0 ? _b : [];
        this.autoIncrement = !!((_c = collectionSchema.key) === null || _c === void 0 ? void 0 : _c.generator);
        this.tableName = collectionSchema.name;
        this.indexNames = collectionSchema.indices.map(function (index) { return KeyUtils.getDBKeyFromKeyPath(index.path); });
        this.initWriteFun(mode);
        this.reqErrorObserver = function (error) { var _a; return (_a = _this.transaction) === null || _a === void 0 ? void 0 : _a.notifyTransactionFail(error); };
        if (engineAspects === null || engineAspects === void 0 ? void 0 : engineAspects.table) {
            this.applyAspectPatch();
        }
    }
    NativeEngineTable.prototype.query = function (query, aggregate) {
        return __awaiter(this, void 0, void 0, function () {
            var req, resp, entries;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        req = {
                            command: Command.GET_ALL,
                            dbName: this.dbName,
                            tableName: this.tableName,
                            where: this.getClientQueryCondition(query),
                        };
                        return [4 /*yield*/, this.sendRequest(req)];
                    case 1:
                        resp = _a.sent();
                        assertResp(resp, 'query', this.reqErrorObserver);
                        if (!resp.result || !resp.result.length) {
                            return [2 /*return*/, []];
                        }
                        entries = resp.result.map(function (value) { return [
                            KeyUtils.getKeyFromValue(value, _this.keyPath),
                            KeyUtils.deleteUnionKeyIfNeed(value, _this.keyPath),
                        ]; });
                        if (aggregate === QueryAggregateType.COUNT) {
                            return [2 /*return*/, entries.length];
                        }
                        if (aggregate === QueryAggregateType.KEYS) {
                            return [2 /*return*/, entries.map(function (_a) {
                                    var _b = __read(_a, 1), key = _b[0];
                                    return key;
                                })];
                        }
                        if (aggregate === QueryAggregateType.VALUES) {
                            return [2 /*return*/, entries.map(function (_a) {
                                    var _b = __read(_a, 2), value = _b[1];
                                    return value;
                                })];
                        }
                        return [2 /*return*/, entries];
                }
            });
        });
    };
    NativeEngineTable.prototype.supports = function () {
        return true;
    };
    NativeEngineTable.prototype.index = function (indexName) {
        var indexSchema = this.collectionSchema.indices.filter(function (index) { return index.name === indexName; });
        if (!indexSchema.length) {
            throw new IndexNotExist(indexName);
        }
        return new NativeEngineIndex(this.dbName, this, indexSchema[0], this.mode, this.engineAspects);
    };
    NativeEngineTable.prototype.getAll = function (query, count) {
        return __awaiter(this, void 0, void 0, function () {
            var req, resp;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        req = {
                            command: Command.GET_ALL,
                            dbName: this.dbName,
                            tableName: this.tableName,
                            where: query
                                ? new QueryConditionBuilder()
                                    .addKeyQueryCondition(this.keyPath, query)
                                    .setLimit(count !== null && count !== void 0 ? count : Number.MAX_VALUE)
                                    .build()
                                : undefined,
                        };
                        return [4 /*yield*/, this.sendRequest(req)];
                    case 1:
                        resp = _a.sent();
                        assertResp(resp, 'getAll', this.reqErrorObserver);
                        if (!resp.result || !resp.result.length) {
                            return [2 /*return*/, []];
                        }
                        return [2 /*return*/, resp.result.map(function (value) { return KeyUtils.deleteUnionKeyIfNeed(value, _this.keyPath); })];
                }
            });
        });
    };
    NativeEngineTable.prototype.getAllKeys = function (query, count) {
        return __awaiter(this, void 0, void 0, function () {
            var values, keys;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getAll(query !== null && query !== void 0 ? query : undefined, count)];
                    case 1:
                        values = _a.sent();
                        if (!values) {
                            return [2 /*return*/, []];
                        }
                        keys = values.map(function (value) { return KeyUtils.getKeyFromValue(value, _this.keyPath); });
                        return [2 /*return*/, keys];
                }
            });
        });
    };
    NativeEngineTable.prototype.get = function (query) {
        return __awaiter(this, void 0, void 0, function () {
            var req, resp;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        req = {
                            command: Command.GET,
                            dbName: this.dbName,
                            tableName: this.tableName,
                            where: new QueryConditionBuilder().addKeyQueryCondition(this.keyPath, query).build(),
                        };
                        return [4 /*yield*/, this.sendRequest(req)];
                    case 1:
                        resp = _a.sent();
                        assertResp(resp, 'get', this.reqErrorObserver);
                        if (resp.result) {
                            return [2 /*return*/, KeyUtils.deleteUnionKeyIfNeed(resp.result, this.keyPath)];
                        }
                        return [2 /*return*/, null];
                }
            });
        });
    };
    NativeEngineTable.prototype.openCursor = function (query, direction) {
        return __awaiter(this, void 0, void 0, function () {
            var dataArray;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getAll(query)];
                    case 1:
                        dataArray = _a.sent();
                        if (!dataArray) {
                            return [2 /*return*/, null];
                        }
                        return [2 /*return*/, new NativeEngineCursor(dataArray, this, direction !== null && direction !== void 0 ? direction : 'next', this.mode, this.engineAspects)];
                }
            });
        });
    };
    NativeEngineTable.prototype.initWriteFun = function (mode) {
        var _this = this;
        if (mode === TransactionMode.VERSION_CHANGE) {
            this.createIndex = this.createIndexFun;
            /* nativeDB will delete index when init db connection by auto,
             * so we don't need to deleteIndex by call this function
             **/
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            this.deleteIndex = (function (indexName) { });
        }
        if (mode !== TransactionMode.READ_ONLY) {
            this.delete = this.deleteData;
            this.bulkDelete = this.bulkDeleteData;
            this.clear = this.deleteData;
            this.insert = this.insertAndUpdate;
            this.upsert = (function (value) {
                return _this.insertAndUpdate(value, true);
            });
            this.bulkUpsert = this.bulkUpsertData;
        }
    };
    /**
     * Native db does not support incremental modification of the library table structure.
     * The full library table interface has been passed in during initialization,
     * so we do not need to implement index create function, because native db implement it when init
     * @param indexName
     * @param key
     * @returns
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    NativeEngineTable.prototype.createIndexFun = function (indexName, key) {
        var indexSchema = this.collectionSchema.indices.filter(function (index) { return index.name === indexName; });
        if (!indexSchema.length) {
            throw new IndexNotExist(indexName);
        }
        return new NativeEngineIndex(this.dbName, this, indexSchema[0], this.mode);
    };
    NativeEngineTable.prototype.bulkDeleteData = function (keys) {
        return __awaiter(this, void 0, void 0, function () {
            var dbKeys, req, resp;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        dbKeys = keys.map(function (key) { return KeyUtils.normalizeUnionKeyValue(key); });
                        req = {
                            command: Command.DELETE,
                            dbName: this.dbName,
                            tableName: this.tableName,
                            where: keys
                                ? new QueryConditionBuilder()
                                    .addCondition(KeyUtils.getDBKeyFromKeyPath(this.keyPath), dbKeys, ClientQueryConditionType.IN)
                                    .build()
                                : undefined,
                        };
                        return [4 /*yield*/, this.sendRequest(req)];
                    case 1:
                        resp = _a.sent();
                        assertResp(resp, 'bulkDelete', this.reqErrorObserver);
                        return [2 /*return*/];
                }
            });
        });
    };
    NativeEngineTable.prototype.bulkUpsertData = function (values) {
        return __awaiter(this, void 0, void 0, function () {
            var dbValues, req, resp;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        dbValues = values
                            .map(function (value) { return KeyUtils.addUnionKeyIfNeed(value, _this.keyPath); })
                            .map(function (value) {
                            return {
                                tableName: _this.tableName,
                                data: value,
                            };
                        });
                        req = {
                            command: Command.UPDATE_MUTIL_TABLE_DATA,
                            dbName: this.dbName,
                            multiData: dbValues,
                        };
                        return [4 /*yield*/, this.sendRequest(req)];
                    case 1:
                        resp = _a.sent();
                        assertResp(resp, 'bulkUpsert', this.reqErrorObserver);
                        return [2 /*return*/, values.map(function (value) { return KeyUtils.getKeyFromValue(value, _this.keyPath); })];
                }
            });
        });
    };
    NativeEngineTable.prototype.insertAndUpdate = function (value, allowUpdate) {
        return __awaiter(this, void 0, void 0, function () {
            var dbValue, req, resp;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        dbValue = KeyUtils.addUnionKeyIfNeed(value, this.keyPath);
                        req = {
                            command: allowUpdate ? Command.UPDATE : Command.INSERT,
                            dbName: this.dbName,
                            tableName: this.tableName,
                            data: dbValue,
                        };
                        return [4 /*yield*/, this.sendRequest(req)];
                    case 1:
                        resp = _a.sent();
                        assertResp(resp, allowUpdate ? 'upsert' : 'insert', this.reqErrorObserver);
                        return [2 /*return*/, KeyUtils.getKeyFromValue(value, this.keyPath)];
                }
            });
        });
    };
    NativeEngineTable.prototype.deleteData = function (query) {
        return __awaiter(this, void 0, void 0, function () {
            var req, resp;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        req = {
                            command: Command.DELETE,
                            dbName: this.dbName,
                            tableName: this.tableName,
                            where: query ? new QueryConditionBuilder().addKeyQueryCondition(this.keyPath, query).build() : undefined,
                        };
                        return [4 /*yield*/, this.sendRequest(req)];
                    case 1:
                        resp = _a.sent();
                        assertResp(resp, query ? 'delete' : 'clear', this.reqErrorObserver);
                        return [2 /*return*/];
                }
            });
        });
    };
    NativeEngineTable.prototype.sendRequest = function (req) {
        var _a, _b;
        if ((_a = this.transaction) === null || _a === void 0 ? void 0 : _a.transactionID) {
            req.transaction = this.transaction.transactionID;
        }
        var respPromise = ClientProxy.send(req);
        (_b = this.transaction) === null || _b === void 0 ? void 0 : _b.addResponsePromise(respPromise);
        return respPromise;
    };
    NativeEngineTable.prototype.getClientQueryCondition = function (query) {
        var e_1, _a, e_2, _b, _c;
        var queryBuilder = new QueryConditionBuilder();
        if (query.whereClauses.length > 1) {
            throw new WhereOrUnsupportedError();
        }
        if (!query.whereClauses.length) {
            return queryBuilder.build();
        }
        var _d = __read(query.whereClauses, 1), whereClause = _d[0];
        try {
            for (var _e = __values(Object.entries(whereClause)), _f = _e.next(); !_f.done; _f = _e.next()) {
                var _g = __read(_f.value, 2), key = _g[0], predicate = _g[1];
                if (isKey(predicate)) {
                    queryBuilder.addKeyQueryCondition(key, predicate);
                    continue;
                }
                if (this.isEmptyPredicate(predicate)) {
                    continue;
                }
                var inKeys = predicate[WhereOperator.In];
                if (Array.isArray(inKeys)) {
                    queryBuilder.addCondition(KeyUtils.getDBKeyFromKeyPath(key), inKeys.map(KeyUtils.normalizeUnionKeyValue), ClientQueryConditionType.IN);
                }
                var ninKeys = predicate[WhereOperator.NotIn];
                if (Array.isArray(ninKeys)) {
                    queryBuilder.addCondition(KeyUtils.getDBKeyFromKeyPath(key), ninKeys.map(KeyUtils.normalizeUnionKeyValue), ClientQueryConditionType.NOT_IN);
                }
                try {
                    for (var _h = (e_2 = void 0, __values(Object.entries(predicate))), _j = _h.next(); !_j.done; _j = _h.next()) {
                        var _k = __read(_j.value, 2), op = _k[0], pivot = _k[1];
                        if (pivot === undefined) {
                            continue;
                        }
                        if (op === WhereOperator.NotEquals) {
                            queryBuilder.addCondition(KeyUtils.getDBKeyFromKeyPath(key), [KeyUtils.normalizeUnionKeyValue(pivot)], ClientQueryConditionType.NOT_IN);
                            continue;
                        }
                        var query_1 = (_c = {},
                            _c[WhereOperator.Equals] = IDBKeyRange.only(pivot),
                            _c[WhereOperator.GreaterThan] = IDBKeyRange.lowerBound(pivot, true),
                            _c[WhereOperator.GreaterOrEqualThan] = IDBKeyRange.lowerBound(pivot, false),
                            _c[WhereOperator.LessThan] = IDBKeyRange.upperBound(pivot, true),
                            _c[WhereOperator.LessOrEqualThan] = IDBKeyRange.upperBound(pivot, false),
                            _c)[op];
                        if (query_1 !== undefined) {
                            queryBuilder.addKeyQueryCondition(key, query_1);
                        }
                    }
                }
                catch (e_2_1) { e_2 = { error: e_2_1 }; }
                finally {
                    try {
                        if (_j && !_j.done && (_b = _h.return)) _b.call(_h);
                    }
                    finally { if (e_2) throw e_2.error; }
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_f && !_f.done && (_a = _e.return)) _a.call(_e);
            }
            finally { if (e_1) throw e_1.error; }
        }
        if (query.limit !== undefined) {
            queryBuilder.setLimit(query.limit);
        }
        queryBuilder.setOffset(query.offset);
        if (query.order !== undefined) {
            // transform union keys to single column key
            queryBuilder.addOrder(KeyUtils.getDBKeyFromKeyPath(query.order.path), query.order.type);
        }
        return queryBuilder.build();
    };
    // check whether the predicate is an empty object
    NativeEngineTable.prototype.isEmptyPredicate = function (predicate) {
        return !predicate || Object.values(predicate).every(function (value) { return value === undefined; });
    };
    /**
     * bind the hook stub to the table object
     */
    NativeEngineTable.prototype.applyAspectPatch = function () {
        var _a;
        var tableAspects = (_a = this.engineAspects) === null || _a === void 0 ? void 0 : _a.table;
        tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.get.patch(this, 'get');
        tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.getAll.patch(this, 'getAll');
        tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.getAllKeys.patch(this, 'getAllKeys');
        tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.index.patch(this, 'index');
        tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.openCursor.patch(this, 'openCursor');
        if (this.mode === TransactionMode.VERSION_CHANGE) {
            tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.createIndex.patch(this, 'createIndex');
            tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.deleteIndex.patch(this, 'deleteIndex');
        }
        if (this.mode === TransactionMode.READ_WRITE || this.mode === TransactionMode.VERSION_CHANGE) {
            tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.delete.patch(this, 'delete');
            tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.insert.patch(this, 'insert');
            tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.upsert.patch(this, 'upsert');
            tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.clear.patch(this, 'clear');
            tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.query.patch(this, 'query');
            tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.bulkDelete.patch(this, 'bulkDelete');
            tableAspects === null || tableAspects === void 0 ? void 0 : tableAspects.bulkUpsert.patch(this, 'bulkUpsert');
        }
    };
    return NativeEngineTable;
}());
export { NativeEngineTable };
//# sourceMappingURL=table.js.map